import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faDownload, faBalanceScale, faSpinner } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../api/axios';
import CardThumbnail, { getThumbnailUrl } from '../../components/CardThumbnail';
import { readPath } from '../../seo/config';

const ConstitutionKanban = ({ data }) => {
    const { t } = useTranslation();
    const tr = (key) => t(`contentCard:${key}`);
    const [downloadingCards, setDownloadingCards] = React.useState(new Set());

   const handleDownload = async (data) => {
        setDownloadingCards(prev => new Set(prev).add(data.id));
        try {
            if (!data.manual?.uri) {
                console.error('No manual URI found');
                return;
            }

            const res = await axiosInstance.get(`/fsmanuals/${data.manual.uri}`, {
                responseType: 'blob',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                    Expires: '0'
                }
            });

            // Create a blob URL for the file
            const fileBlob = new Blob([res.data], { type: res.data.type });
            const url = URL.createObjectURL(fileBlob);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = data.title || 'constitution-document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading manual:', error);
        } finally {
            setDownloadingCards(prev => {
                const newSet = new Set(prev);
                newSet.delete(data.id);
                return newSet;
            });
        }
   };


 
    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

    return (
        <motion.div initial="hidden" animate="visible">
            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mt-6">
                {data?.map((d) => {
                    const year = d.date ? new Date(d.date).getFullYear() : 'Unknown';

                    return (
                        <motion.div
                            key={d.id}
                            variants={cardVariants}
                            whileHover={{ scale: 1.03 }}
                            className="rounded-lg overflow-hidden transition-all duration-300
                                border border-gray-200 dark:border-gray-700
                                bg-white dark:bg-gray-800
                                shadow-lg hover:shadow-xl"
                        >
                            <div className="relative p-4">
                                {/* Journal Icon */}
                                <div className="absolute top-2 right-2 text-gray-500 dark:text-gray-400">
                                    <FontAwesomeIcon icon={faBalanceScale} className="w-5 h-5" />
                                </div>

                                <CardThumbnail
                                    icon={faBalanceScale}
                                    title={d.title}
                                    imageSrc={getThumbnailUrl(d?.thumbnail?.uri)}
                                    objectFit="contain"
                                />
                                {/* Content */}
                                <div className="mt-4">
                                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 truncate">
                                        <Link
                                            to={readPath(d.id, d.title, 'constitution')}
                                            state={{ id: d.id, documentType: 'constitution' }}
                                            className="hover:text-[#41A6D7]"
                                        >
                                            {d.title}
                                        </Link>
                                    </h3>
                                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed overflow-hidden" style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical'
                                    }}>{d.description}</p>

                                    {/* Meta Info */}
                                    <div className="flex justify-between items-center mt-3">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">{year}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-between mt-4">
                                    <Link
                                        to={readPath(d.id, d.title, 'constitution')}
                                        state={{ id: d.id, documentType: 'constitution' }}
                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-500 text-sm flex items-center"
                                    >
                                        <FontAwesomeIcon icon={faEye} className="w-3 h-3 mr-1" />
                                        {tr('read')}
                                    </Link>

                                    <button 
                                    onClick={() => handleDownload(d)}
                                    disabled={downloadingCards.has(d.id)}
                                    className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-500 text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed">
                                        <FontAwesomeIcon 
                                            icon={downloadingCards.has(d.id) ? faSpinner : faDownload} 
                                            className={`w-3 h-3 mr-1 ${downloadingCards.has(d.id) ? 'animate-spin' : ''}`} 
                                        />
                                        {tr('download')}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>
        </motion.div>
    );
};

export default ConstitutionKanban;
